/**
 * Furnizori — lista cu fatete si fisa unui furnizor.
 *
 * Filtrarea, sortarea si paginarea se fac PE SERVER (ruta filtreaza peste `v_supplier_overview` si
 * intoarce si contoarele de fatete). Clientul nu reimplementeaza niciunul dintre filtre: `sold`,
 * `stoc` si `payable` se compara ca intregi in unitati minore, iar o sortare facuta in JS pe cifre
 * formatate ar reordona 691 de furnizori dupa text, nu dupa bani.
 *
 * `stocGtSold` are TREI valori (`true` / `false` / `null`) si asa ramane pana in interfata: `null`
 * inseamna „nu exista snapshot", nu „nu e cazul". Rosul de pe coloana STOC („nu se plateste marfa
 * nevanduta") nu are voie sa dispara pentru ca lipseste o data.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { api } from '../lib/api.ts';
import { chei } from '../lib/query-keys.ts';
import type { SuppliersQuery } from '../../../../functions/api/schemas.ts';

/** Toate campurile sunt optionale: `limit`, `offset` si `sort` au implicite pe server. */
export type FiltreFurnizori = Partial<SuppliersQuery>;

export interface FurnizorLista {
  id: number;
  name: string;
  kind: string;
  cuiNorm: string | null;
  countryCode: string | null;
  currencyDefault: string | null;
  paymentTermsDays: number | null;
  creditLimitMinor: number | null;
  isPrepaid: boolean;
  payOnlySoldMinusStoc: boolean;
  needsReview: boolean;
  bancaRaw: string | null;
  /** Codurile derivate din IBAN — singura sursa fiabila de banca. */
  bankCodes: string[];
  categories: string[];
  soldMinor: number | null;
  stocMinor: number | null;
  payableMinor: number | null;
  stocGtSold: boolean | null;
  snapshotIsEstimated: boolean;
  ibanCount: number;
  ibanInvalidCount: number;
  commentCount: number;
  ruleCount: number;
}

export interface Fateta {
  value: string;
  count: number;
}

export interface RaspunsFurnizori {
  page: { limit: number; offset: number; total: number; grandTotal: number };
  facets: {
    bank: Fateta[];
    category: Fateta[];
    country: Fateta[];
    currency: Fateta[];
    quality: Fateta[];
    kind: Fateta[];
  };
  suppliers: FurnizorLista[];
}

export function useFurnizori(
  filtre?: FiltreFurnizori,
  optiuni?: { activ?: boolean },
): UseQueryResult<RaspunsFurnizori, Error> {
  return useQuery({
    queryKey: chei.furnizori.lista(filtre as Readonly<Record<string, unknown>> | undefined),
    queryFn: ({ signal }) =>
      api.get<RaspunsFurnizori>('/furnizori', {
        interogare: {
          q: filtre?.q,
          cui: filtre?.cui,
          bank: filtre?.bank,
          category: filtre?.category,
          country: filtre?.country,
          currency: filtre?.currency,
          quality: filtre?.quality,
          prepaid: filtre?.prepaid,
          stocGtSold: filtre?.stocGtSold,
          limit: filtre?.limit,
          offset: filtre?.offset,
          sort: filtre?.sort,
        },
        semnal: signal,
      }),
    enabled: optiuni?.activ !== false,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// GET /api/furnizori/:id
// ---------------------------------------------------------------------------

export interface ContBancarFurnizor {
  id: number;
  ordinal: number;
  ibanRaw: string;
  ibanNorm: string;
  ibanValid: boolean;
  bankId: number | null;
  bankCode: string | null;
  bankCodeRaw: string | null;
  currency: string | null;
  isPrimary: boolean;
  flagged: boolean;
  flagNote: string | null;
}

export interface MetricaSaptamanaFurnizor {
  isoYear: number;
  isoWeek: number;
  asOfDate: string;
  /** Cele 7 saptamani venite din fisier sunt marcate estimate: bannerul spune adevarul. */
  isEstimated: boolean;
  soldMinor: number | null;
  stocMinor: number | null;
  payableMinor: number | null;
  stocGtSold: boolean | null;
  /** Sold agregat peste mai multe monede — nefiabil, se afiseaza cu avertisment. */
  soldMixedCurrency: boolean;
}

export interface RaspunsFurnizor {
  supplier: {
    id: number;
    name: string;
    /** Ce zice fisierul, cand cineva a corectat numele. */
    nameImport: string;
    nameOverride: string | null;
    /** Codul efectiv: corectura, apoi IBAN-ul, apoi eticheta bruta. */
    bankCode: string | null;
    bankCodeOverride: string | null;
    /** Versiunea sidecarului `supplier_app`; `0` cand nu exista inca. */
    appVersion: number;
    nameErp: string | null;
    nameMismatch: boolean;
    nameMojibake: boolean;
    kind: string;
    cuiRaw: string | null;
    cuiNorm: string | null;
    /** `1` = cui ok, `0` = cui gresit, `null` = necontrolat. Trei stari, nu doua. */
    cuiCheckOk: number | null;
    countryCode: string | null;
    currencyDefault: string | null;
    monedaRaw: string | null;
    bancaRaw: string | null;
    paymentTermsDays: number | null;
    termsValidated: boolean;
    creditLimitMinor: number | null;
    creditLimitCurrency: string | null;
    limitValidated: boolean;
    dsRaw: string | null;
    taxInv: boolean;
    sursePlata: string | null;
    isPrepaid: boolean;
    payOnlySoldMinusStoc: boolean;
    needsReview: boolean;
    reviewReason: string | null;
    excelRow: number | null;
    version: number;
  };
  latest: {
    soldMinor: number | null;
    stocMinor: number | null;
    payableMinor: number | null;
    stocGtSold: boolean | null;
    isEstimated: boolean;
  };
  bankAccounts: ContBancarFurnizor[];
  identifiers: Array<{
    id: number;
    kind: string;
    valueRaw: string;
    valueNorm: string;
    isPrimary: boolean;
    verified: boolean;
    conflictNote: string | null;
  }>;
  aliases: Array<{
    id: number;
    alias: string;
    source: string;
    confidence: string;
    confirmed: boolean;
  }>;
  categories: Array<{ categoryId: number; name: string; isPrimary: boolean; categRaw: string }>;
  weekHistory: MetricaSaptamanaFurnizor[];
  prepaid: Array<{
    listOrdinal: number;
    listLabel: string | null;
    balanceMinor: number | null;
    currency: string;
    asOfDate: string;
  }>;
  wme: Array<{
    asOfDate: string;
    accountCode: string;
    currency: string;
    valoareMinor: number | null;
    restMinor: number | null;
    weekrestMinor: number | null;
  }>;
  stoc: Array<{
    asOfDate: string;
    qtyE3: number | null;
    valueMinor: number | null;
    vatRateE6: number | null;
    valueWithVatMinor: number | null;
  }>;
  /** Cele 11 note de pe coloana L, fixate pe furnizor si vizibile pe FIECARE rand al lui. */
  rules: Array<{
    id: number;
    kind: string;
    body: string;
    authorDisplay: string;
    createdAt: number;
    pinned: boolean;
  }>;
  history: Array<{
    entryId: number;
    businessDate: string;
    runLabel: string;
    currency: string;
    amountMinor: number;
    amountRonMinor: number;
    status: string;
    postponeCount: number;
    invoiceRefCount: number;
  }>;
}

/**
 * Fisa unui furnizor.
 *
 * Ruta cere un ID NUMERIC (`Id` = `z.coerce.number().int().positive()`), nu un CUI — si la fel il
 * cere si ruta de UI, `/furnizori/$id`. CUI-ul nu poate fi identificator: 85 de furnizori nu au
 * deloc, iar doua CUI-uri apar pe cate doua firme (D5).
 */
export function useFurnizor(
  id: number | null | undefined,
): UseQueryResult<RaspunsFurnizor, Error> {
  return useQuery({
    queryKey: chei.furnizori.unul(id ?? 0),
    queryFn: ({ signal }) => api.get<RaspunsFurnizor>(`/furnizori/${id ?? 0}`, { semnal: signal }),
    enabled: typeof id === 'number' && id > 0,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// PATCH /api/furnizori/:id
// ---------------------------------------------------------------------------

export interface VariabilePatchFurnizor {
  id: number;
  version?: number;
  /** `null` sterge suprascrierea si revine la valoarea din fisier. */
  bankCodeOverride?: string | null;
  nameDisplayOverride?: string | null;
  reason?: string | null;
}

export interface RaspunsPatchFurnizor {
  id: number;
  name: string;
  nameImport: string;
  nameOverride: string | null;
  bankCode: string | null;
  bankCodeOverride: string | null;
  version: number;
}

/**
 * Corectarea numelui sau a bancii unui furnizor.
 *
 * Se invalideaza SI ziua, SI matricea, SI lista de furnizori: corectura se vede in toate trei,
 * fiindca toate citesc acum prin `v_supplier_overview` sau prin acelasi `COALESCE`. Daca am
 * invalida doar ecranul curent, acelasi furnizor ar aparea cu doua nume in doua taburi deschise.
 */
export function usePatchFurnizor(): UseMutationResult<
  RaspunsPatchFurnizor,
  Error,
  VariabilePatchFurnizor
> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsPatchFurnizor, Error, VariabilePatchFurnizor>({
    mutationKey: chei.mutatii.patchFurnizor(),
    mutationFn: ({ id, ...corp }) =>
      api.patch<RaspunsPatchFurnizor>(`/furnizori/${id}`, corp),
    retry: false,
    onSuccess: (_raspuns, variabile) => {
      void queryClient.invalidateQueries({ queryKey: chei.furnizori.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.furnizori.unul(variabile.id) });
      void queryClient.invalidateQueries({ queryKey: chei.zi.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.matrice.tot() });
    },
  });
}
